import {useGetDataInfoRange} from "api/api-hooks/datasets-hooks";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import {
    ICollapseDataTableNodesProps,
    ICollapseDataTableProps,
    ITreeTableColumnBodyProps
} from "components/dashboard/dataset-collapse-data-table/interfaces/interfaces";
import {DatasetDataInfo} from "models/dataset-models";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {TreeTable} from "primereact/treetable";
import React, {useEffect, useState} from "react";
import {addToDate, truncateDate} from "utils/time-utils";
import "./DatasetCollapseDataTable.scss";


const DatasetCollapseDataTable = (props: ICollapseDataTableProps) => {

    const lagOffset = props.config?.lagOffset || 1;
    const lagUnits = props.config?.lagUnits || "YEARS";
    const alignDateTo = props.config?.alignStartTo || "YEARS";
    const tempDate = addToDate(new Date(), -lagOffset, lagUnits);

    const [dateFrom, setDateFrom] = useState<Date>(truncateDate(tempDate, alignDateTo));
    const [dateTo, setDateTo] = useState<Date>(new Date());
    const [dataNodes, setDataNodes] = useState<Array<{}>>();
    const [uniqueYears, setUniqueYears] = useState<string[]>([]);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const datasetList = props.datasets.map(dataset => dataset.identifier);

    const {data: datasetData, isLoading} = useGetDataInfoRange(datasetList, {
            timeStart: dateFrom.toISOString(),
            timeEnd: dateTo.toISOString()
        },
    );

    function convertDataToNodes(datasets: DatasetDataInfo[]) {
        const result: ICollapseDataTableNodesProps[] = [];

        function getMonthName(dateString: string) {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat(navigator.language, {month: "long"}).format(date);
        }

        const yearsSet = new Set<string>();

        datasets.forEach((dataset: DatasetDataInfo) => {
            const identifier = dataset.identifier;
            const data = dataset.data;
            const decimalPlaces = dataset.decimalPlaces

            data?.forEach((entry) => {
                const date = new Date(entry.key);
                const year = date.getFullYear().toString();
                yearsSet.add(year);

                const monthName = getMonthName(entry.key);
                const day = date.getDate();

                let monthEntry = result.find((item) => item.data.name === monthName);

                if (!monthEntry) {
                    monthEntry = {
                        key: result.length.toString(),
                        data: {
                            name: monthName,
                            averages: {},
                        },
                        children: [],
                    };
                    result.push(monthEntry);
                }

                const propertyName = `${identifier}_${year}`;
                let dayEntry = monthEntry.children?.find((item) => item.data.name === day.toString());

                if (dayEntry) {
                    dayEntry.data[propertyName] = `${entry.value.toFixed(decimalPlaces)}`;
                } else {
                    dayEntry = {
                        key: `${monthEntry.key}-${monthEntry.children?.length}`,
                        data: {
                            name: day.toString(),
                            [propertyName]: `${entry.value}`
                        },
                    };
                    monthEntry.children?.push(dayEntry);
                }

                if (!monthEntry.data.averages[propertyName]) {
                    monthEntry.data.averages[propertyName] = [];
                }
                monthEntry.data.averages[propertyName].push(entry.value);
            });
        });

        setUniqueYears(Array.from(yearsSet));


        result.forEach((monthEntry) => {
            Object.keys(monthEntry.data.averages).forEach((datasetKey) => {
                const datasetValues = monthEntry.data.averages[datasetKey];
                const total = datasetValues.reduce((acc: number, val: number) => acc + val, 0);
                const average = total / datasetValues.length || 0;
                monthEntry.data[datasetKey] = average.toFixed(2);
            });
        });
        return result;
    }

    useEffect(() => {
        if (datasetData && datasetData.length > 0 && !isLoading) {
            const convertedData = convertDataToNodes(datasetData);
            setDataNodes(convertedData);
        }
    }, [isLoading]);

    const handleExpand = (e: any) => {
        const rowSet = new Set(expandedRows);
        rowSet.add(e.node.key);
        setExpandedRows([...rowSet]);
    };

    const handleCollapse = (e: any) => {
        const rowSet = new Set(expandedRows);
        rowSet.delete(e.node.key);
        setExpandedRows([...rowSet]);
    };

    const rowClassName = (node: ICollapseDataTableNodesProps) => {
        let className = {"bg-primary-reverse": false};
        if (expandedRows.includes(node.key)) {
            className["bg-primary-reverse"] = true;
        }
        return className;
    };

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header="Date" rowSpan={3} style={{width: "10%"}}/>
                <Column header="Data" colSpan={uniqueYears.length * datasetData?.length!}/>
            </Row>
            <Row>
                {uniqueYears.map((year, index) => {
                    return <Column key={index} header={year} colSpan={datasetData?.length}/>;
                })}
            </Row>
            <Row>
                {uniqueYears
                    .sort((a, b) => parseInt(b) - parseInt(a))
                    .map((_) =>
                        datasetData?.map((data, index) => {
                            const title = props.datasets.find(dataset => dataset.identifier === data.identifier)?.overriddenTitle || data.title;
                            return <Column
                                key={index}
                                header={title}
                            ></Column>;
                        })
                    )}
            </Row>
        </ColumnGroup>
    );

    const yearBodyTemplate = (data: any, column: ITreeTableColumnBodyProps) => {
        const columnIdentifierID = column.field.split(":")[0];
        const unit = datasetData && datasetData.find(data => data.identifier.split(":")[0] === columnIdentifierID)?.unit || "";
        const currentValue = data.data[column.field] ?? "-";
        return `${currentValue} ${unit}`;
    };

    return (
        <div>
            <div className="flex gap-2 mb-2 z">
                <div className="flex flex-column gap-2">
                    <label>Date from:</label>
                    <CalendarWrapper value={dateFrom} showTime={false} onChange={e => setDateFrom(e)}/>
                </div>
                <div className="flex flex-column gap-2">
                    <label>Date to:</label>
                    <CalendarWrapper value={dateTo} showTime={false} onChange={e => setDateTo(e)}/>
                </div>
            </div>
            {dataNodes && datasetData && dataNodes.length > 0 && (
                <TreeTable
                    value={dataNodes}
                    headerColumnGroup={headerGroup}
                    showGridlines
                    onExpand={handleExpand}
                    onCollapse={handleCollapse}
                    rowClassName={rowClassName}
                >
                    <Column field="name" expander></Column>
                    {uniqueYears
                        .sort((a, b) => parseInt(b) - parseInt(a))
                        .map((year) =>
                            datasetData.map((dataset, index) => (
                                <Column
                                    key={index}
                                    field={`${dataset.identifier}_${year}`}
                                    className="text-center"
                                    body={yearBodyTemplate}
                                ></Column>
                            ))
                        )}
                </TreeTable>
            )}
        </div>
    );
};

export default DatasetCollapseDataTable;